import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

// Lucide icon imports
import {
  Check,
  FileText,
  AlertCircle,
  Users,
  Tag,
  Bell,
  Settings,
} from 'lucide-mui';

import moment from 'moment';
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { RootState } from '../../../reducers';
import {
  markNotificationAsRead,
  clearAllNotifications,
} from '../dashboardSlice';
import NotificationsEmptyState from './NotificationsEmptyState';

interface NotificationsProps {
  open: boolean;
  onClose: () => void;
}

const Notifications: React.FC<NotificationsProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { dashboard } = useSelector((state: RootState) => state);
  const { notifications } = dashboard;
  useEffect(() => {
    const currentTitle = document.title;
    const baseTitle = currentTitle.replace(/^\(\d+\)\s/, '');
    if (notifications.length > 0) {
      document.title = `(${
        notifications.filter((n) => !n.read).length
      }) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, [notifications]);

  const handleNotificationClick = (id: string, link?: string) => {
    if (link) {
      window.location.href = link;
    }
    dispatch(markNotificationAsRead(id));
    onClose();
  };

  const handleDismissAll = () => {
    dispatch(clearAllNotifications());
    onClose();
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'transcription':
        return <FileText />;
      case 'tag':
        return <Tag />;
      case 'team':
        return <Users />;
      case 'error':
        return <AlertCircle />;
      case 'processing':
        return <Settings />;
      default:
        return <Bell />;
    }
  };

  const hasAnyUnreadNotifications = notifications.some((n) => !n.read);

  // Sort notifications by timestamp in descending order (newest first)
  const sortedNotifications = [...notifications].sort(
    (a, b) => b.timestamp - a.timestamp
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {notifications.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1">Notifications</Typography>
          {hasAnyUnreadNotifications && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleDismissAll}
              sx={{ textTransform: 'none' }}
            >
              Mark all as read
            </Button>
          )}
        </Box>
      )}
      <List sx={{ width: '100%', bgcolor: 'background.paper', py: 0 }}>
        {notifications.length > 0 ? (
          sortedNotifications.map(
            ({ id, type, title, message, timestamp, read, link }) => (
              <ListItem
                key={id}
                alignItems="flex-start"
                onClick={() => handleNotificationClick(id, link)}
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  py: 2,
                  px: theme.spacing(2),
                  opacity: read ? 0.7 : 1,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    opacity: 1,
                    '& .dismiss-button': {
                      opacity: 1,
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      height: '40px',
                    }}
                  >
                    {getIcon(type)}
                  </ListItemIcon>
                </Box>
                <ListItemText
                  sx={{ ml: 2 }}
                  primary={
                    <Typography variant="body1" fontWeight={!read ? 700 : 500}>
                      {title}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      sx={{ display: 'inline', fontSize: '0.75rem' }}
                      component="span"
                      variant="body2"
                      color="text.primary"
                    >
                      {message}
                    </Typography>
                  }
                />
                <Box
                  sx={{
                    minWidth: 80,
                    textAlign: 'right',
                    ml: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ opacity: 0.8 }}
                    >
                      {moment(timestamp).calendar(null, {
                        sameDay: '[Today]',
                        nextDay: '[Tomorrow]',
                        nextWeek: 'dddd',
                        lastDay: '[Yesterday]',
                        lastWeek: '[Last] dddd',
                        sameElse: 'DD/MM/YYYY',
                      })}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ opacity: 0.8 }}
                    >
                      {moment(timestamp).format('h:mm A')}
                    </Typography>
                  </Box>
                  <IconButton
                    className="dismiss-button"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(markNotificationAsRead(id));
                    }}
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      color: 'text.secondary',
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            )
          )
        ) : (
          <Box sx={{ p: 2 }}>
            <NotificationsEmptyState />
          </Box>
        )}
      </List>
    </Dialog>
  );
};

export default Notifications;
